import React from 'react';
import { useQuery } from '@apollo/client/react';
import { Link } from 'react-router';

import { Table } from '@repo/ui/Table';
import { PAGE_SCHEMA, Pagination, decodePaging } from '@repo/ui/Pagination';
import { useZodQuery } from '@repo/hooks/useZodQuery';
import { useUser } from '@repo/hooks/useUser';
import { coerce } from '@repo/util/coerceForTable';
import { useFilterPreference } from '@repo/hooks/useFilterPreference';
import { toQueryList } from '@repo/util/toQueryList';
import { coerceValue } from '@repo/util/coerceValueForQuery';
import { Form } from '@repo/ui/Form';
import { z } from 'zod';

import { POLICIES } from '@/lib/Authorization';
import GET_SWITCHES_FOR_ACCOUNT from '../gql/getSwitchesForAccount.gql';
import { useDefaultSwitch } from '../hooks/useDefaultSwitch';
import { protocolLabel } from '../lib/protocolLabel';
import { truncateName } from '@/lib/truncateName';
import SwitchesTableFilter from './SwitchesTableFilter';

export const ACCOUNT_SWITCHES_TABLE_KEY = 'account-switches';

const ACCOUNT_SWITCHES_QUERY_SCHEMA = PAGE_SCHEMA.extend({
  name: z.string().default(''),
});

const PAGE_SIZE = 10;

export const DefaultBadge = () => (
  <span className="px-2 py-0.5 ml-2 text-xs font-bold rounded-full bg-blue-100 text-blue-900 dark:bg-blue-700 dark:text-blue-100">
    Default
  </span>
);

const SwitchesForAccountTable = ({ accountId }: { accountId: string }) => {
  const user = useUser();
  const filter = useZodQuery({
    schema: ACCOUNT_SWITCHES_QUERY_SCHEMA,
    ignoreList: ['page'],
    coerce,
  });
  const { defaultSwitch } = useDefaultSwitch(accountId);

  useFilterPreference({
    tableKey: ACCOUNT_SWITCHES_TABLE_KEY,
    userId: user.id,
    schema: ACCOUNT_SWITCHES_QUERY_SCHEMA,
    filter,
  });

  const { page, ...queryFields } = filter.queryValues;
  const queryList = toQueryList(queryFields, coerceValue, ['name']);

  const { loading, error, data } = useQuery(GET_SWITCHES_FOR_ACCOUNT, {
    variables: { ...decodePaging(page, PAGE_SIZE), queryList, id: accountId },
    notifyOnNetworkStatusChange: true,
  });

  if (error) console.error(error);

  const { edges, pageInfo } = data?.switchesForAccount ?? {};
  const canShow = POLICIES['switches:show'](user);
  const detailPath = (id: string) => `/a/${accountId}/switches/${id}`;

  return (
    <div>
      <Form {...filter} variant="horizontal" className="mb-6">
        <SwitchesTableFilter />
      </Form>
      <Table
        loading={loading}
        pageSize={PAGE_SIZE}
        data={edges?.map(({ node }) => node) ?? []}
        dataType="switches"
        error={!!error}
        columns={[
          {
            title: 'Name',
            Cell({ entry: { id, name } }) {
              const label = truncateName(name);
              return (
                <span className="whitespace-nowrap">
                  {canShow ? (
                    <Link to={detailPath(id)} className="underline" title={name}>
                      {label}
                    </Link>
                  ) : (
                    <span title={name}>{label}</span>
                  )}
                  {id === defaultSwitch?.id && <DefaultBadge />}
                </span>
              );
            },
          },
          {
            title: 'API Key',
            Cell({ entry: { apiKey } }) {
              return <p>{apiKey?.name ?? '—'}</p>;
            },
          },
          {
            title: 'SMS forwarding',
            Cell({ entry: { smsProtocol } }) {
              return <p>{protocolLabel(smsProtocol)}</p>;
            },
          },
          {
            title: 'MMS forwarding',
            Cell({ entry: { mmsProtocol } }) {
              return <p>{protocolLabel(mmsProtocol)}</p>;
            },
          },
        ]}
      />
      {!error ? <Pagination pageInfo={pageInfo} filter={filter} /> : null}
    </div>
  );
};

export default SwitchesForAccountTable;
